import { Bar, BarChart, CartesianGrid, Legend, LabelList, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { CapacitySnapshot } from '../../../utils/panorama/panoramaCapacity';
import type { Status } from '../../../utils/panorama/panoramaPeopleRows';
import { CHART_COLORS } from '../vacationOverviewPage.definitions';
import { ChartTooltip } from './ChartTooltip';
import { EmptyChart } from './DashboardCard';
import { useCompactChart } from './useCompactChart';
import { CompactBarAxes, getCompactBarAxesConfig } from './compactBarAxes';

interface CapacityChartProps {
	snapshot: CapacitySnapshot;
	onWeekClick?: (weekStart: string, statuses: Status | Status[]) => void;
}

export function CapacityChart({ snapshot, onWeekClick }: CapacityChartProps) {
	const { isCompact, rowHeight } = useCompactChart();

	if (snapshot.baseCount === 0) {
		return <EmptyChart message="Nenhuma pessoa na base com os filtros atuais." height={280} />;
	}

	const handleBarClick = (weekStart: string, statuses: Status | Status[]) => {
		onWeekClick?.(weekStart, statuses);
	};

	const config = getCompactBarAxesConfig(isCompact);

	const chartHeight = isCompact ? rowHeight(snapshot.weeks.length) || 280 : '100%';

	return (
		<div className="h-full min-h-[280px]">
			<ResponsiveContainer width="100%" height={chartHeight}>
			<BarChart data={snapshot.weeks} layout={config.layout} margin={config.margin}>
				<CartesianGrid stroke={CHART_COLORS.grid} vertical={!isCompact} />
				<CompactBarAxes isCompact={isCompact} categoryKey="weekRangeLabel" />
				<Tooltip content={<ChartTooltip unit=" pessoa(s)" formatLabel={row => `${row.weekLabel} - ${row.weekRangeLabel}`} />} cursor={{ fill: '#f1f5f9' }} />
				<Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
				{isCompact ? (
					<ReferenceLine
						x={snapshot.limitCount}
						stroke={CHART_COLORS.alert}
						strokeDasharray="4 4"
						label={{ value: `limite ${snapshot.capacityLimitPercent}%`, position: 'top', fontSize: 11, fill: CHART_COLORS.alert }}
					/>
				) : (
					<ReferenceLine
						y={snapshot.limitCount}
						stroke={CHART_COLORS.alert}
						strokeDasharray="4 4"
						label={{ value: `limite ${snapshot.capacityLimitPercent}% (${snapshot.limitCount})`, position: 'insideTopRight', fontSize: 11, fill: CHART_COLORS.alert }}
					/>
				)}
				<Bar
					dataKey="onVacation"
					name="Em férias"
					fill="#0d9488"
					onClick={data => onWeekClick && handleBarClick(data.payload.weekStart, 'on-vacation')}
					cursor={onWeekClick ? 'pointer' : 'default'}
					radius={config.barRadius}
				>
					{isCompact && <LabelList dataKey="onVacation" position="right" fontSize={11} />}
				</Bar>
				<Bar
					dataKey="scheduled"
					name="Agendados"
					fill="#0284c7"
					onClick={data => onWeekClick && handleBarClick(data.payload.weekStart, 'scheduled')}
					cursor={onWeekClick ? 'pointer' : 'default'}
					radius={config.barRadius}
				>
					{isCompact && <LabelList dataKey="scheduled" position="right" fontSize={11} />}
				</Bar>
				<Bar
					dataKey="pending"
					name="Em aprovação"
					fill="#7c3aed"
					onClick={data => onWeekClick && handleBarClick(data.payload.weekStart, ['pending', 'approval-urgent'])}
					cursor={onWeekClick ? 'pointer' : 'default'}
					radius={config.barRadius}
				>
					{isCompact && <LabelList dataKey="pending" position="right" fontSize={11} />}
				</Bar>
			</BarChart>
		</ResponsiveContainer>
		</div>
	);
}
